import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TotpSetupStep from './TotpSetupStep';
import { isWebauthnSupported } from '../auth/webauthnHelpers';
import { navigateAway } from '../lib/tabCloseSuppression';

// ADDED (2026-09-20): the persistent backstop for choosing a sign-in
// method — AccountCompletionGuard.jsx sends anyone with
// user.needsMfaSetup here on EVERY navigation, not just once right after
// registration. That gap was real and confirmed live: Register.jsx's own
// inline choice screen (still there, for the smooth first-time case) is
// only ever mounted for the few seconds right after registering — someone
// who navigated away before finishing it (e.g. a failed passkey ceremony's
// own "Go to SSO Portal" link) landed on an ordinary page with a fully
// valid session and no second factor at all, because nothing re-checked
// afterward. This page is deliberately its OWN route (not extracted out of
// Register.jsx into a shared component) — same convention
// ChangePasswordRequired.jsx already uses for the analogous
// must-change-password case.
//
// Unlike Register.jsx's version, this can't read the typed phone number
// out of local component state (there isn't any — this may be a page load
// long after registration, e.g. after an admin's "Reset SMS Sign-in").
// contactPhone comes from AuthContext's own `user`, sourced fresh from
// accountUpdate.js's /me.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
const WEBAUTHN_CEREMONY_URL = process.env.REACT_APP_WEBAUTHN_CEREMONY_URL || 'https://auth.bescomapps.com/webauthn-ceremony';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(10, 20, 40, 0.38)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '16px',
  padding: '56px 48px',
  width: '100%',
  maxWidth: '500px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.45)',
  textAlign: 'center',
};

const textMuted = 'rgba(255, 255, 255, 0.75)';

const bigBtn = {
  width: '100%', padding: '14px', marginBottom: '10px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff', border: 'none', borderRadius: '10px',
  fontSize: '16px', fontWeight: '600', cursor: 'pointer',
};
const bigBtnDisabled = { ...bigBtn, opacity: 0.6, cursor: 'not-allowed' };

export default function MfaSetupRequired() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [error, setError] = useState(null);
  // Which button is in flight — null | 'totp' | 'passkey' | 'code' — not just whether ANY of them is, so
  // clicking one doesn't flip the OTHER buttons' own busy label text too (real bug, seen live 2026-09-22 on
  // ClaimAccount.jsx's identical screen: choosing Passkey made the code button say "Please wait…" too).
  const [busy, setBusy] = useState(null);
  const [mode, setMode] = useState(null); // null | 'totp'
  const [totpChallenge, setTotpChallenge] = useState(null);
  const [totpLoading, setTotpLoading] = useState(false);

  function finish() {
    navigate(user?.profileComplete ? '/dashboard' : '/complete-profile');
  }

  async function afterChoiceMade() {
    // Re-resolve `user` so the guard sees needsMfaSetup:false immediately —
    // without this, navigating straight to /dashboard would just bounce
    // right back here for one more round trip (harmless, just slower).
    await refreshUser();
    finish();
  }

  async function handleChoosePasskey() {
    setBusy('passkey');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-webauthn/start-ceremony`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.token) {
        setError(data.error || 'Could not start passkey setup. Please try again.');
        setBusy(null);
        return;
      }
      const redirectBack = encodeURIComponent(window.location.origin + '/setup-mfa');
      navigateAway(`${WEBAUTHN_CEREMONY_URL}?token=${encodeURIComponent(data.token)}&redirect=${redirectBack}`);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setBusy(null);
    }
  }

  async function handleStartTotp() {
    setBusy('totp');
    setMode('totp');
    setTotpLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/setup-totp/challenge`, { credentials: 'include' });
      const data = await res.json();
      if (!data.raw?.config_url) {
        setError(data.error || 'Could not start authenticator setup. Please try again.');
        setMode(null);
        setBusy(null);
        return;
      }
      setTotpChallenge(data);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setMode(null);
      setBusy(null);
    } finally {
      setTotpLoading(false);
    }
  }

  async function handleConfirmTotp({ code }) {
    setTotpLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/setup-totp/step`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      if (data.done) {
        await afterChoiceMade();
        return;
      }
      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Incorrect code. Please try again.');
        return;
      }
      setError('Unexpected response. Please try again.');
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
    } finally {
      setTotpLoading(false);
    }
  }

  // One-time code: the code goes to the phone if the account has one, else
  // the email (backend lib/attributeMfa.js).
  async function handleChooseCode() {
    setBusy('code');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/set-code-mfa`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not set up code sign-in. Please try again.');
        setBusy(null);
        return;
      }
      await afterChoiceMade();
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setBusy(null);
    }
  }

  const hasPhone = /^\+91\d{10}$/.test(user?.contactPhone || '');
  const hasEmail = !!user?.contactEmail;

  return (
    <div style={page}>
      <div style={card}>
        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px', textAlign: 'left',
          }}>
            {error}
          </div>
        )}

        {mode === 'totp' ? (
          totpChallenge?.raw?.config_url ? (
            <>
              <TotpSetupStep
                configUrl={totpChallenge.raw.config_url}
                loading={totpLoading}
                onSubmit={handleConfirmTotp}
                dark
                mandatory
              />
              <button
                type="button"
                onClick={() => { setMode(null); setBusy(null); setTotpChallenge(null); setError(null); }}
                disabled={totpLoading}
                style={{
                  width: '100%', marginTop: '10px', padding: '12px',
                  background: 'none', border: '1px solid rgba(255,255,255,0.35)', color: textMuted,
                  borderRadius: '10px', fontSize: '14px', fontWeight: '500',
                  cursor: totpLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Choose a different method
              </button>
            </>
          ) : (
            <p style={{ color: textMuted, fontSize: '14px' }}>Loading…</p>
          )
        ) : (
          <>
            <p style={{ fontSize: '32px', margin: '0 0 8px' }}>🔒</p>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#D4A94C', margin: '0 0 8px' }}>
              Choose how you'll sign in
            </h1>
            <p style={{ fontSize: '14px', color: textMuted, margin: '0 0 24px', lineHeight: 1.5 }}>
              Your account doesn't have a sign-in method set up yet — you'll need one every time you
              sign in, so pick whichever is easiest for you.
            </p>
            <button type="button" onClick={handleStartTotp} disabled={!!busy} style={busy ? bigBtnDisabled : bigBtn}>
              📱 Use an Authenticator App
            </button>
            {isWebauthnSupported() && (
              <button type="button" onClick={handleChoosePasskey} disabled={!!busy} style={busy ? bigBtnDisabled : bigBtn}>
                {busy === 'passkey' ? 'Redirecting…' : '🔑 Use a Passkey'}
              </button>
            )}
            {(hasPhone || hasEmail) && (
              <button type="button" onClick={handleChooseCode} disabled={!!busy} style={busy ? bigBtnDisabled : bigBtn}>
                {busy === 'code' ? 'Please wait…' : '🔐 Phone/Email OTP'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
